import { useState } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  FormControl,
  FormLabel,
  Input,
  Textarea,
  Button,
  Divider
} from '@chakra-ui/react';
import { MdStar, MdAccessTime } from 'react-icons/md';
import { CreateTaskData } from '../../../types/task';
import { useTranslation } from 'react-i18next';

interface TaskCreateModalProps {
  isOpen: boolean;
  onClose: () => void;
  onCreateTask: (data: CreateTaskData) => Promise<boolean>;
}

export const TaskCreateModal: React.FC<TaskCreateModalProps> = ({
  isOpen,
  onClose,
  onCreateTask
}) => {
  const { t } = useTranslation(['v0/tasks', 'v0/projects', 'v0/common']);
  const [formData, setFormData] = useState<CreateTaskData>({
    title: '',
    notes: '',
    done: false,
    vital: false,
    urgent: false,
    fixed: false,
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const success = await onCreateTask(formData);
    if (success) {
      // 重置表单
      setFormData({
        title: '',
        notes: '',
        done: false,
        vital: false,
        urgent: false,
        fixed: false,
      });
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="lg"
    >
      <ModalOverlay />
      <ModalContent borderRadius="xl" mx={4}>
        <ModalHeader>{t('v0/tasks:form.createTitle')}</ModalHeader>
        <ModalCloseButton />
        <ModalBody pb={6}>
          <form onSubmit={handleSubmit} className="space-y-4">
            <FormControl isRequired>
              <FormLabel>{t('v0/tasks:form.titleField')}</FormLabel>
              <Input
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                placeholder={t('v0/tasks:form.titleField')}
                borderRadius="lg"
              />
            </FormControl>

            <FormControl>
              <FormLabel>{t('v0/tasks:form.notesField')}</FormLabel>
              <Textarea
                value={formData.notes || ''}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                placeholder={t('v0/common:addNotesOrDescription')}
                borderRadius="lg"
                minHeight="100px"
              />
            </FormControl>

            <FormControl>
              <FormLabel>{t('v0/tasks:form.dates.dueDate')}</FormLabel>
              <Input
                type="datetime-local"
                value={formData.dueAt || ''}
                onChange={(e) => setFormData({ ...formData, dueAt: e.target.value })}
                borderRadius="lg"
              />
            </FormControl>

            <Divider />

            <div className="grid grid-cols-2 gap-4">
              <FormControl display="flex" alignItems="center">
                <FormLabel htmlFor="vital" mb="0">
                  {t('v0/tasks:form.priority.vital')}
                </FormLabel>
                <Button
                  id="vital"
                  size="sm"
                  rightIcon={<MdStar />}
                  colorScheme={formData.vital ? 'red' : 'gray'}
                  variant={formData.vital ? 'solid' : 'outline'}
                  onClick={() => setFormData({ ...formData, vital: !formData.vital })}
                  ml="auto"
                >
                  {formData.vital ? t('v0/common:actions.yes') : t('common:actions.no')}
                </Button>
              </FormControl>

              <FormControl display="flex" alignItems="center">
                <FormLabel htmlFor="urgent" mb="0">
                  {t('v0/tasks:form.priority.urgent')}
                </FormLabel>
                <Button
                  id="urgent"
                  size="sm"
                  rightIcon={<MdAccessTime />}
                  colorScheme={formData.urgent ? 'orange' : 'gray'}
                  variant={formData.urgent ? 'solid' : 'outline'}
                  onClick={() => setFormData({ ...formData, urgent: !formData.urgent })}
                  ml="auto"
                >
                  {formData.urgent ? t('v0/common:actions.yes') : t('common:actions.no')}
                </Button>
              </FormControl>
            </div>

            <Button
              mt={6}
              colorScheme="teal"
              width="100%"
              type="submit"
              borderRadius="lg"
              size="lg"
            >
              {t('v0/tasks:form.createTitle')}
            </Button>
          </form>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};